import {
  Component,
  effect,
  inject,
  input,
  OnInit,
  output,
  PLATFORM_ID,
  Renderer2,
  signal,
} from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { AuthService } from '../../services/auth.service';
import { DynamicForm } from '../dynamic-form/dynamic-form';
import { Loading } from '../loading/loading';
import { ErrorModalComponent, MessageType } from '../error-modal/error-modal.component';
import { CONSUMER_FORM } from '../../../features/user/user-form-type';

@Component({
  selector: 'app-user-data-modal',
  imports: [DynamicForm, Loading, ErrorModalComponent],
  templateUrl: './user-data-modal.html',
  styleUrl: './user-data-modal.scss',
})
export class UserDataModal implements OnInit {
  isOpen = input<boolean>(false);
  saved = output<void>();

  private authService = inject(AuthService);
  private renderer = inject(Renderer2);
  private platformId = inject(PLATFORM_ID);

  isLoading = signal(false);
  showError = signal(false);
  errorType = signal<MessageType>('error');
  errorMessage = signal('');

  formData = signal<any>({});
  userData = signal<any>({});
  catalogs = signal<any>({});

  userSchema = CONSUMER_FORM.map((i) => ({ ...i, colSpan: 1 }));

  constructor() {
    effect(() => {
      if (!isPlatformBrowser(this.platformId)) return;
      if (this.isOpen()) {
        this.renderer.setStyle(document.body, 'overflow', 'hidden');
      } else {
        this.renderer.removeStyle(document.body, 'overflow');
      }
    });
  }

  async ngOnInit() {
    this.catalogs.set(
      await this.authService.getCatalogs(['business-rol', 'cities', 'genders']),
    );

    const user = this.authService.getUser();
    if (user) {
      this.userData.set(
        this.parseDataToForm({
          ...user.profile,
          ...(user.profile?.consumer_extra ? user.profile.consumer_extra : {}),
        }),
      );
    }
  }

  onFormChange(data: any) {
    this.formData.set(data);
  }

  async onSubmit(formValue: any) {
    try {
      this.isLoading.set(true);
      await this.authService.updateProfile(
        {
          ...formValue.data,
          cities: [formValue.data.cities],
          redirect_url: isPlatformBrowser(this.platformId) ? window.location.href : '',
          enabled_required: false,
        },
        'profile',
      );

      const userData = await this.authService.getUserData();
      this.authService.saveUserData(userData);
      
      if (isPlatformBrowser(this.platformId)) {
        this.renderer.removeStyle(document.body, 'overflow');
      }
      this.saved.emit();
    } catch (ex: any) {
      const errors = this.authService.parseErrors(ex.error);
      this.errorType.set('error');
      this.errorMessage.set(errors || 'Error al guardar el perfil, por favor intente de nuevo');
      this.showError.set(true);
    } finally {
      this.isLoading.set(false);
    }
  }

  private isArray(value: any): boolean {
    return Array.isArray(value);
  }

  private parseDataToForm(data: any) {
    const fields = this.userSchema;
    return Object.keys(data).reduce((acc: any, key) => {
      const item = data[key];
      const field = fields.find((f: any) => f.key === key);
      if (item && item.value) {
        acc[key] = item.value;
      } else if (item && item.content) {
        acc[key] = item.content;
      } else if (item && this.isArray(item) && field?.type === 'schedule') {
        acc[key] = item;
      } else if (
        item &&
        this.isArray(item) &&
        field?.type === 'multiselect' &&
        field.withCustomOptions
      ) {
        field.options = item.map((i: any) => ({
          ...i,
          label: i[field.optionLabel || 'label'],
          value: i[field.optionValue || 'value'],
        }));
        acc[key] = field.options;
      } else if (item && (field?.type === 'select' || field?.type === 'radio') && this.isArray(item)) {
        acc[key] = item[0]?.value || '';
      } else if (item && this.isArray(item)) {
        acc[key] = item.map((i: any) => i.value);
      } else if (key === 'image') {
        acc['image_id'] = { id: item?.id, url: item?.url };
      } else {
        acc[key] = item;
      }
      return acc;
    }, {});
  }
}
